import { Suspense, useMemo } from 'react'
import * as THREE from 'three'
import { Shape, Vector3 } from 'three'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../../hooks/state/useRoof'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'

export const TrapezPlatte = (): JSX.Element => {
  const {
    metalColor,
    roofGlassDepth,
    roughness,
    amplitude,
    frequenz,
    slatAngle,
  } = useRoof()
  const { verandaTyp, userWidth, depth, isQubus, roofLogWidth } = useMeasures()

  const plateRotation = slatAngle

  const length = plateRotation
    ? isQubus
      ? userWidth - verandaTyp.log.width
      : userWidth - verandaTyp.roofBar.depth * 2
    : isQubus
    ? depth - verandaTyp.log.depth * 2
    : roofLogWidth

  const width = plateRotation
    ? depth - verandaTyp.log.depth * 2
    : userWidth - verandaTyp.log.width

  const amp = amplitude / 100
  const freq = frequenz / 100

  const topFlatRatio = 0.3
  const bottomFlatRatio = 0.4
  const topFlatWidth = freq * topFlatRatio
  const bottomFlatWidth = freq * bottomFlatRatio
  const remainingWidthForSlopes = freq - topFlatWidth - bottomFlatWidth
  const slopeWidth = remainingWidthForSlopes / 2

  if (slopeWidth < 0) {
    console.warn(
      'TrapezPlatte: Negative slopeWidth. Adjust topFlatRatio and bottomFlatRatio to sum less than 1.0.'
    )
  }

  const segmentRepeatWidth = slopeWidth * 2 + topFlatWidth + bottomFlatWidth

  const thickness = roofGlassDepth

  const shape = useMemo(() => {
    const newShape = new Shape()
    let currentX = 0

    newShape.moveTo(currentX, 0)

    while (currentX < width) {
      newShape.lineTo(currentX + slopeWidth, amp)
      newShape.lineTo(currentX + slopeWidth + topFlatWidth, amp)
      newShape.lineTo(currentX + slopeWidth * 2 + topFlatWidth, 0)
      newShape.lineTo(currentX + segmentRepeatWidth, 0)
      currentX += segmentRepeatWidth
    }

    newShape.lineTo(currentX, -thickness)

    let backX = currentX
    while (backX > 0) {
      const x_tal_unten_calc = backX - bottomFlatWidth
      const x_schraeg_unten_ende_calc = x_tal_unten_calc - slopeWidth
      const x_flach_oben_unten_calc = x_schraeg_unten_ende_calc - topFlatWidth
      const x_schraeg_oben_anfang_calc = x_flach_oben_unten_calc - slopeWidth

      const x_tal_unten = Math.max(0, x_tal_unten_calc)
      const x_schraeg_unten_ende = Math.max(0, x_schraeg_unten_ende_calc)
      const x_flach_oben_unten = Math.max(0, x_flach_oben_unten_calc)
      const x_schraeg_oben_anfang = Math.max(0, x_schraeg_oben_anfang_calc)

      if (backX - segmentRepeatWidth < 0 && backX > 0) {
        newShape.lineTo(0, -thickness)
        break
      } else if (backX <= 0) {
        break
      }

      newShape.lineTo(x_tal_unten, 0 - thickness)
      newShape.lineTo(x_schraeg_unten_ende, amp - thickness)
      newShape.lineTo(x_flach_oben_unten, amp - thickness)
      newShape.lineTo(x_schraeg_oben_anfang, 0 - thickness)

      backX -= segmentRepeatWidth
    }
    newShape.closePath()
    return newShape
  }, [
    amp,
    freq,
    width,
    slopeWidth,
    topFlatWidth,
    bottomFlatWidth,
    segmentRepeatWidth,
    thickness,
  ])

  const extrudeSettings = useMemo(
    () => ({
      steps: 1,
      depth: length,
      bevelEnabled: false,
      curveSegments: 1,
    }),
    [length]
  )

  const platePosition = new Vector3(
    plateRotation ? -length / 2 : -width / 2,
    verandaTyp.roofBar.width / 2 + (isQubus ? 0 : 0),
    plateRotation ? width : isQubus ? 0 : 0
  )

  return (
    <Suspense fallback={null}>
      <group
        position={platePosition}
        rotation={plateRotation ? [0, Math.PI / 2, 0] : [0, 0, 0]}
      >
        <mesh castShadow receiveShadow>
          <extrudeGeometry args={[shape, extrudeSettings]} />
          <CachedMeshPhysicalMaterial
            color={metalColor}
            roughness={roughness}
            metalness={0.5}
            side={THREE.FrontSide}
          />
        </mesh>

        <mesh position={[0, -thickness, 0]}>
          <extrudeGeometry args={[shape, extrudeSettings]} />
          <CachedMeshPhysicalMaterial
            color={'white'}
            roughness={roughness}
            metalness={0.5}
            side={THREE.FrontSide}
          />
        </mesh>
      </group>
    </Suspense>
  )
}
